import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import ServiceDetailModal from './ServiceDetailModal';
import type { RuntimeAdminTool, RuntimeInfo, RuntimeService, SourceHealth } from '@/types';
import {
  displayStatusLabel,
  groupRuntimeServices,
  isServiceReachable,
} from '@/utils/runtimeService';
import {
  sortSourceHealthForRuntime,
  sourceHealthDisplayName,
  sourceHealthPillClass,
  sourceHealthPillStatus,
} from '@/utils/sourceHealthRuntime';

function statusClass(status: string): string {
  switch (status) {
    case 'up':
      return 'runtime-dot-up';
    case 'down':
      return 'runtime-dot-down';
    case 'disabled':
      return 'runtime-dot-disabled';
    case 'optional':
      return 'runtime-dot-optional';
    case 'degraded':
      return 'runtime-dot-warn';
    case 'configured':
      return 'runtime-dot-configured';
    default:
      return 'runtime-dot-unknown';
  }
}

function hasDetails(service: RuntimeService): boolean {
  return Boolean(
    service.metrics ||
      service.detail ||
      service.error ||
      service.start_hint ||
      service.restart_hint ||
      service.health ||
      service.url ||
      service.admin_url ||
      service.public_url
  );
}

function pillTitle(service: RuntimeService): string {
  const lines = [displayStatusLabel(service)];
  const h = service.health;
  if (h) {
    lines.push(`Running: ${h.running}`, `Healthy: ${h.healthy}`, `Reachable: ${h.reachable}`);
  }
  if (service.status === 'optional' && service.start_hint) {
    lines.push('', `Start: ${service.start_hint}`);
  }
  return lines.join('\n');
}

function ServicePill({
  service,
  onSelect,
}: {
  service: RuntimeService;
  onSelect: (service: RuntimeService) => void;
}) {
  const { label, status, url, admin_url: adminUrl, public_url: publicUrl } = service;
  const reachable = isServiceReachable(service);
  const href = publicUrl || url || adminUrl;
  const linkTarget = reachable && href ? href : null;
  const title = pillTitle(service);
  const isOptional = status === 'optional';
  const dot = <span className={`runtime-dot ${statusClass(status)}`} aria-hidden />;

  const handleClick = (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey) {
      if (linkTarget) {
        window.open(linkTarget, '_blank', 'noopener,noreferrer');
      }
      return;
    }
    if (hasDetails(service)) {
      onSelect(service);
    }
  };

  const handleAuxClick = (e: React.MouseEvent) => {
    if (e.button === 1 && linkTarget) {
      e.preventDefault();
      window.open(linkTarget, '_blank', 'noopener,noreferrer');
    }
  };

  if (linkTarget) {
    return (
      <a
        href={linkTarget}
        target="_blank"
        rel="noopener noreferrer"
        className="runtime-pill runtime-pill-link"
        title={`${title} — open (${hasDetails(service) ? 'click body for details, ⌘/Ctrl+click same' : linkTarget})`}
        onClick={(e) => {
          if (!e.metaKey && !e.ctrlKey && hasDetails(service)) {
            e.preventDefault();
            onSelect(service);
          }
        }}
      >
        {dot}
        {label}
      </a>
    );
  }

  return (
    <button
      type="button"
      className={`runtime-pill runtime-pill-button ${isOptional ? 'runtime-pill-optional' : ''} ${hasDetails(service) ? '' : 'runtime-pill-nolink'}`}
      title={title}
      disabled={!hasDetails(service)}
      onClick={handleClick}
      onAuxClick={handleAuxClick}
    >
      {dot}
      {label}
    </button>
  );
}

function ServiceGroup({
  title,
  services,
  onSelect,
}: {
  title: string;
  services: RuntimeService[];
  onSelect: (service: RuntimeService) => void;
}) {
  if (services.length === 0) return null;
  return (
    <div className="runtime-service-group">
      <span className="runtime-group-label">{title}</span>
      <div className="runtime-group-pills">
        {services.map((s) => (
          <ServicePill key={s.id} service={s} onSelect={onSelect} />
        ))}
      </div>
    </div>
  );
}

function AdminToolPill({ tool }: { tool: RuntimeAdminTool }) {
  if (!tool.enabled && tool.health !== 'disabled') return null;
  const isDisabled = tool.health === 'disabled';
  const isUp = tool.health === 'up' || tool.health === 'degraded';
  const status = isDisabled ? 'disabled' : isUp ? 'up' : tool.health === 'down' ? 'down' : 'unknown';
  const dot = <span className={`runtime-dot ${statusClass(status)}`} aria-hidden />;
  const titleParts = [tool.display_name];
  if (isDisabled) titleParts.push('Disabled');
  else if (tool.detail) titleParts.push(tool.detail);
  if (!isUp && !isDisabled && tool.public_url) {
    titleParts.push('(unreachable — link may not load)');
  }
  const title = titleParts.join(': ');

  if (tool.public_url && !isDisabled) {
    return (
      <a
        href={tool.public_url}
        target="_blank"
        rel="noopener noreferrer"
        className={`runtime-pill runtime-pill-link ${isUp ? '' : 'runtime-pill-unreachable'}`}
        title={isUp ? `${title} — open ${tool.public_url}` : title}
      >
        {dot}
        {tool.display_name}
      </a>
    );
  }

  return (
    <span
      className={`runtime-pill runtime-pill-button runtime-pill-nolink ${isDisabled ? 'runtime-pill-disabled' : ''}`}
      title={title}
    >
      {dot}
      {tool.display_name}
      {isDisabled ? ' (Disabled)' : ''}
    </span>
  );
}

function AdminToolsGroup({ tools }: { tools: RuntimeAdminTool[] }) {
  const visible = tools.filter((tool) => tool.enabled || tool.health === 'disabled');
  if (visible.length === 0) return null;
  return (
    <div className="runtime-service-group">
      <span className="runtime-group-label">Admin Services</span>
      <div className="runtime-group-pills">
        {visible.map((tool) => (
          <AdminToolPill key={tool.id} tool={tool} />
        ))}
      </div>
    </div>
  );
}

function SourceHealthPill({ health }: { health: SourceHealth }) {
  const label = sourceHealthDisplayName(health.source_name);
  const status = sourceHealthPillStatus(health);
  const dotClass = sourceHealthPillClass(health);
  return (
    <Link
      to="/sources"
      className="runtime-pill runtime-pill-link runtime-pill-source-health"
      title={`${label}: ${status}`}
    >
      <span className={`runtime-dot ${dotClass}`} aria-hidden />
      {label} {status}
    </Link>
  );
}

function SourceHealthGroup({ healthRows }: { healthRows: SourceHealth[] }) {
  if (healthRows.length === 0) return null;
  return (
    <div className="runtime-service-group runtime-service-group--source-health">
      <span className="runtime-group-label">Source Health</span>
      <div className="runtime-group-pills">
        {healthRows.map((row) => (
          <SourceHealthPill key={row.source_name} health={row} />
        ))}
      </div>
    </div>
  );
}

export default function RuntimeStatusBar({ compact = false }: { compact?: boolean }) {
  const [info, setInfo] = useState<RuntimeInfo | null>(null);
  const [sourceHealth, setSourceHealth] = useState<SourceHealth[]>([]);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<RuntimeService | null>(null);
  const [healthModalOpen, setHealthModalOpen] = useState(false);

  useEffect(() => {
    if (!healthModalOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [healthModalOpen]);

  const load = useCallback(async () => {
    try {
      const data = await api.dashboard.runtimeInfo();
      setInfo(data);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Runtime status unavailable');
    }
  }, []);

  const loadSourceHealth = useCallback(async () => {
    try {
      const rows = await api.sources.health();
      setSourceHealth(sortSourceHealthForRuntime(rows));
    } catch {
      /* non-fatal */
    }
  }, []);

  useEffect(() => {
    load();
    loadSourceHealth();
    const id = setInterval(() => {
      load();
      loadSourceHealth();
    }, 30000);
    return () => clearInterval(id);
  }, [load, loadSourceHealth]);

  const groups = info ? groupRuntimeServices(info.services) : [];
  const healthPills = sourceHealth.slice(0, 8);

  const healthContent = (
    <>
      <div className="runtime-status-left">
        {info?.public_urls && (
          <span className="runtime-public-urls muted" title="Canonical public URLs">
            <a href={info.public_urls.admin_ui_url} target="_blank" rel="noopener noreferrer">
              Public
            </a>
            {info.public_urls.lan_fallback_url ? (
              <>
                {' · '}
                <a href={info.public_urls.lan_fallback_url} target="_blank" rel="noopener noreferrer">
                  LAN
                </a>
              </>
            ) : null}
          </span>
        )}
      </div>

      <div className="runtime-status-center">
        {groups.map((group) => (
          <ServiceGroup
            key={group.title}
            title={group.title}
            services={group.services}
            onSelect={setSelected}
          />
        ))}
        {info?.admin_tools && info.admin_tools.length > 0 ? (
          <AdminToolsGroup tools={info.admin_tools} />
        ) : null}
        <SourceHealthGroup healthRows={healthPills} />
        {!info && !error && <span className="muted runtime-loading">Loading services…</span>}
        {error && (
          <span className="runtime-error" title={error}>
            Status unavailable
          </span>
        )}
      </div>
    </>
  );

  if (compact) {
    return (
      <>
        <div className="runtime-status-compact" data-testid="runtime-status-compact">
          <button
            type="button"
            className="btn btn-sm runtime-health-trigger"
            onClick={() => setHealthModalOpen(true)}
          >
            System Health
          </button>
        </div>
        {healthModalOpen ? (
          <div
            className="system-health-modal-backdrop"
            role="presentation"
            onClick={() => setHealthModalOpen(false)}
          >
            <div
              className="system-health-modal"
              role="dialog"
              aria-modal="true"
              aria-label="System health"
              onClick={(event) => event.stopPropagation()}
              data-testid="system-health-modal"
            >
              <div className="system-health-modal__header">
                <h2>System Health</h2>
                <button
                  type="button"
                  className="mobile-nav-close-btn"
                  aria-label="Close"
                  onClick={() => setHealthModalOpen(false)}
                >
                  ×
                </button>
              </div>
              <div className="runtime-status-bar runtime-status-bar--modal">{healthContent}</div>
            </div>
          </div>
        ) : null}
        {selected && (
          <ServiceDetailModal
            service={selected}
            workers={info?.workers}
            queues={info?.queues}
            onClose={() => setSelected(null)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="runtime-status-bar" aria-label="Runtime status">
        {healthContent}
      </div>

      {selected && (
        <ServiceDetailModal
          service={selected}
          workers={info?.workers}
          queues={info?.queues}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}
